import React, { useState } from 'react';
import { Row, Col, Card, CardBody, Button, FormGroup, Label, Input } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SelectDispenseDateModal from '../../commonComponents/SelectDispenseDateModal.js';
import DropdownComponent from '../../../../components/Dropdown.js';

const reasonOptions = [
    { value: 'expired', label: 'Expired' },
    { value: 'wasted', label: 'Wasted' },
    { value: 'discontinued', label: 'Discontinued' },
];

function DestroyDestructionForm({
    data,
    dispensedt,
    setDispensedt,
    openDispenseDateModal,
    setOpenDispenseDateModal,
    onDestroyed,
    manualMode = false,
    facId: manualFacId,
    canCreate = true,
    commonValues,
    commonFieldsValid = true,
}) {
    const row = data?.[0] && typeof data[0] === 'object' ? data[0] : {};
    const { patName, patID, drugLabelName, qtyDispensed, rxNo, dispenseDt, facId: searchFacId, controlledDrug } = row;
    const searchRemainingQty =
        row.remainingQty ??
        row.RemainingQty ??
        row.remainingQuantity ??
        row.RemainingQuantity;
    const [error, setError] = useState(null);

    const resolveDispensedLimit = (source) => {
        const raw = manualMode ? source?.DispensedQuantity : qtyDispensed;
        if (raw === '' || raw === null || raw === undefined) return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    };
    const resolveRemainingLimit = (source) => {
        const raw = manualMode ? source?.RemainingQuantity : searchRemainingQty;
        if (raw === '' || raw === null || raw === undefined) return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    };

    const emptyToUndefined = (value, originalValue) =>
        typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value;

    const DestroyDestructionValidationSchema = Yup.object({
        Reason: Yup.string().required('Reason is required'),
        ...(manualMode
            ? {
                  ResidentName: Yup.string(),
                  DrugName: Yup.string(),
                  DispenseDate: Yup.string(),
                  DispensedQuantity: Yup.number()
                      .transform(emptyToUndefined)
                      .typeError('Dispensed quantity must be a number')
                      .positive('Dispensed quantity must be a positive number'),
                  RemainingQuantity: Yup.number()
                      .transform(emptyToUndefined)
                      .typeError('Remaining quantity must be a number')
                      .min(0, 'Remaining quantity cannot be negative'),
              }
            : {}),
        Quantity: (manualMode ? Yup.number().transform(emptyToUndefined) : Yup.number().required('Quantity is required'))
            .test('is-less-than-dispensed', 'Quantity cannot be greater than dispensed quantity', function (value) {
                const limit = resolveDispensedLimit(this.parent);
                if (Number.isFinite(limit) && value > limit) {
                    return this.createError({
                        message: `Quantity cannot be greater than dispensed quantity of ${limit}`,
                    });
                }
                return true;
            })
            .test('is-less-than-remaining', 'Quantity cannot be greater than remaining quantity', function (value) {
                const limit = resolveRemainingLimit(this.parent);
                if (Number.isFinite(limit) && limit === 0) {
                    return this.createError({
                        message: 'Remaining quantity is already zero, so you cannot add further quantity here',
                    });
                }
                if (Number.isFinite(limit) && value > limit) {
                    return this.createError({
                        message: `Quantity cannot be greater than remaining quantity of ${limit}`,
                    });
                }
                return true;
            }),
    });
    const initialValues = {
        Reason: '',
        Quantity: '',
        NDC: '',
        RxNumber: '',
        ResidentName: '',
        DrugName: '',
        DispenseDate: '',
        DispensedQuantity: '',
        RemainingQuantity: '',
    };
    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        setFieldValue,
    } = useFormik({
        initialValues,
        validationSchema: DestroyDestructionValidationSchema,
        onSubmit: async (values) => {
            if (onDestroyed) {
                const rowFacId = manualMode ? manualFacId : searchFacId;
                if (manualMode && !rowFacId) {
                    toast.error('You must select a facility from facility dropdown');
                    return;
                }
                onDestroyed({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    rxNumber: manualMode ? values.RxNumber : rxNo,
                    facId: rowFacId,
                    residentName: manualMode ? values.ResidentName : patName,
                    residentId: manualMode ? '' : patID,
                    drugName: manualMode ? values.DrugName : drugLabelName,
                    dispensedQuantity: manualMode
                        ? String(values.DispensedQuantity ?? '')
                        : String(qtyDispensed ?? ''),
                    ndc: values.NDC,
                    dispenseDate: manualMode ? values.DispenseDate : dispenseDt,
                    quantity: Number(values.Quantity),
                    destructionDate: new Date().toISOString(),
                    destructionMethod: commonValues?.DestructionMethod ?? '',
                    specify: commonValues?.Specify ?? '',
                    reason: values.Reason,
                    nurseName: commonValues?.NurseName ?? '',
                    witnessName: commonValues?.WitnessName ?? '',
                    controlledDrug: manualMode ? false : controlledDrug,
                    isManual: manualMode,
                });
                toast.success('Item added successfully!');
            }
            resetForm();
            setError(null);
        },
    });

    const dispensedLimit = resolveDispensedLimit(values);
    const remainingLimit = resolveRemainingLimit(values);
    const isRemainingQtyZero = Number.isFinite(remainingLimit) && remainingLimit === 0;
    const remainingQtyErrorMessage = isRemainingQtyZero
        ? 'Remaining quantity is already zero, so you cannot add further quantity here'
        : `Quantity cannot be greater than remaining quantity of ${remainingLimit}`;
    const isRemainingQtyError =
        Number.isFinite(remainingLimit) && values.Quantity !== '' && Number(values.Quantity) > remainingLimit;
    const isMissingManualFacility = manualMode && !manualFacId;
    const isManualEntryEmpty =
        manualMode &&
        [
            values.RxNumber,
            values.ResidentName,
            values.DrugName,
            values.DispenseDate,
            values.DispensedQuantity,
            values.RemainingQuantity,
            values.NDC,
            values.Quantity,
        ].every((value) => value === '' || value === null || value === undefined);
    const showFieldErrors = !manualMode;
    const handleQuantityChange = (e) => {
        handleChange(e);
        const quantity = Number(e.target.value);
        if (isRemainingQtyZero) {
            setError(remainingQtyErrorMessage);
        } else if (Number.isFinite(remainingLimit) && e.target.value !== '' && quantity > remainingLimit) {
            setError(remainingQtyErrorMessage);
        } else {
            setError(null);
        }
    };
 const handleButtonClick = () => {
        if (isRemainingQtyZero) {
            setError(remainingQtyErrorMessage);
            return;
        }
        if (Number.isFinite(remainingLimit) && Number(values.Quantity) > remainingLimit) {
            setError(remainingQtyErrorMessage);
            return;
        }
        if (Number.isFinite(dispensedLimit) && Number(values.Quantity) > dispensedLimit) {
            setError(`Quantity cannot be greater than dispensed quantity of ${dispensedLimit}`);
            return;
        }
        setError(null);
        handleSubmit();
    };
    return (
        <>
        <Card className="mb-4 no-radius-card">
            <CardBody>
                {manualMode ? (
                    <>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="RxNumber">Rx #</Label>
                                    <Input
                                        type="text"
                                        name="RxNumber"
                                        id="RxNumber"
                                        placeholder="Rx #"
                                        value={values.RxNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="DispenseDate">Dispense Date</Label>
                                    </div>
                                    <DatePicker
                                        className={`form-control${showFieldErrors && errors.DispenseDate && touched.DispenseDate ? ' is-invalid' : ''}`}
                                        id="DispenseDate"
                                        name="DispenseDate"
                                        placeholderText="MM/DD/YYYY"
                                        dateFormat="MM/dd/yyyy"
                                        maxDate={new Date()}
                                        selected={values.DispenseDate ? new Date(values.DispenseDate) : null}
                                        onChange={(date) =>
                                            setFieldValue('DispenseDate', date ? date.toISOString() : '')
                                        }
                                        onBlur={handleBlur}
                                        autoComplete="off"
                                    />
                                    {showFieldErrors && errors.DispenseDate && touched.DispenseDate && (
                                        <div className="invalid-feedback d-block">{errors.DispenseDate}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="ResidentName">Resident</Label>
                                    </div>
                                    <Input
                                        type="text"
                                        name="ResidentName"
                                        id="ResidentName"
                                        placeholder="Last, First"
                                        value={values.ResidentName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        invalid={showFieldErrors && errors.ResidentName && touched.ResidentName}
                                    />
                                    {showFieldErrors && errors.ResidentName && touched.ResidentName && (
                                        <div className="invalid-feedback d-block">{errors.ResidentName}</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="DrugName">Drug Name</Label>
                                    </div>
                                    <Input
                                        type="text"
                                        name="DrugName"
                                        id="DrugName"
                                        placeholder="Drug name"
                                        value={values.DrugName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        invalid={showFieldErrors && errors.DrugName && touched.DrugName}
                                    />
                                    {showFieldErrors && errors.DrugName && touched.DrugName && (
                                        <div className="invalid-feedback d-block">{errors.DrugName}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="NDC">NDC</Label>
                                    <Input
                                        type="text"
                                        name="NDC"
                                        id="NDC"
                                        placeholder="NDC"
                                        value={values.NDC}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="DispensedQuantity">Dispensed Quantity</Label>
                                    </div>
                                    <Input
                                        type="number"
                                        name="DispensedQuantity"
                                        id="DispensedQuantity"
                                        placeholder="Dispensed Qty"
                                        value={values.DispensedQuantity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        invalid={showFieldErrors && errors.DispensedQuantity && touched.DispensedQuantity}
                                    />
                                    {showFieldErrors && errors.DispensedQuantity && touched.DispensedQuantity && (
                                        <div className="invalid-feedback d-block">{errors.DispensedQuantity}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="RemainingQuantity">Remaining Quantity</Label>
                                    </div>
                                    <Input
                                        type="number"
                                        name="RemainingQuantity"
                                        id="RemainingQuantity"
                                        placeholder="Remaining Qty"
                                        value={values.RemainingQuantity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        invalid={showFieldErrors && errors.RemainingQuantity && touched.RemainingQuantity}
                                    />
                                    {showFieldErrors && errors.RemainingQuantity && touched.RemainingQuantity && (
                                        <div className="invalid-feedback d-block">{errors.RemainingQuantity}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        {isMissingManualFacility && (
                            <div className="text-danger mb-2">
                                Please select a facility from the dropdown before destroying.
                            </div>
                        )}
                    </>
                ) : (
                <Row>
                    <Col md={6}>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Rx #</p>
                            <p>{rxNo}</p>
                        </div>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Resident</p>
                            <p>{patName}</p>
                        </div>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Resident ID</p>
                            <p>{patID}</p>
                        </div>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Remaining Quantity</p>
                            <p>{searchRemainingQty != null && searchRemainingQty !== '' ? searchRemainingQty : '—'}</p>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Dispense Date</p>
                            <p> {new Date(dispenseDt).toLocaleDateString()} </p>
                        </div>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Drug Name</p>
                            <p>{drugLabelName}</p>
                        </div>
                        <div className="d-flex flex-column p-0">
                            <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Dispensed Quantity</p>
                            <p>{qtyDispensed}</p>
                        </div>

                    </Col>
                </Row>
                )}
                <Row className="mt-5 align-items-end">
                    <Col md={4}>
                        <FormGroup className="mb-0">
                            <Label for="Quantity">
                                Quantity {!manualMode && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="number"
                                name="Quantity"
                                id="Quantity"
                                placeholder="Return Quantity"
                                value={values.Quantity}
                                onChange={handleQuantityChange}
                                onBlur={handleBlur}
                                invalid={showFieldErrors && errors.Quantity && touched.Quantity}
                            />
                            {(isRemainingQtyZero || error) && (
                                <div className="invalid-feedback d-block">{isRemainingQtyZero ? remainingQtyErrorMessage : error}</div>
                            )}
                            {showFieldErrors && errors.Quantity && touched.Quantity && !error && !isRemainingQtyZero && (
                                <div className="invalid-feedback d-block">{errors.Quantity}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup className="mb-0">
                            <Label for="Reason">
                                Destruction Reason <span className="text-danger">*</span>
                            </Label>
                            <DropdownComponent
                                options={reasonOptions}
                                placeholder="Select reason..."
                                selectedDropdownValue={values.Reason}
                                setSelectedDropdownValue={(value) => setFieldValue('Reason', value)}
                                invalid={errors.Reason && touched.Reason}
                            />
                            {errors.Reason && touched.Reason && (
                                <div className="invalid-feedback d-block">{errors.Reason}</div>
                            )}
                        </FormGroup>
                    </Col>
                    {canCreate && (
                        <Col md={4} className="d-flex justify-content-end">
                            <Button
                                color="danger"
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                    padding: '10px 20px',
                                    border: 'none',
                                    outline: 'none',
                                }}
                                onClick={handleButtonClick}
                                disabled={
                                    !values.Reason ||
                                    isRemainingQtyZero ||
                                    isRemainingQtyError ||
                                    isMissingManualFacility ||
                                    isManualEntryEmpty ||
                                    !commonFieldsValid
                                }>
                                Destroy
                            </Button>
                        </Col>
                    )}
                </Row>
            </CardBody>
        </Card>
        {!manualMode && (
            <SelectDispenseDateModal
                dispensedt={dispensedt}
                setDispensedt={setDispensedt}
                openDispenseDateModal={openDispenseDateModal}
                setOpenDispenseDateModal={setOpenDispenseDateModal}
                data={data}
            />
        )}
        </>
    );
}

export default DestroyDestructionForm;
